import React, { useState } from "react";
import { Link } from "react-router-dom";
import BtwFiltersPanel, { countActiveFilters } from "./btw-filters-panel";
import { getStatusColor } from "../core/btw-status-colors";

const ViewToggle = ({ view, listTo, boardTo }) => (
  <div className="inline-flex rounded bg-[#091E420F] p-0.5 text-xs font-medium">
    <Link
      to={listTo}
      className={`rounded px-3 py-1 transition ${
        view === "list"
          ? "bg-white text-brand shadow-card"
          : "text-ink-subtle hover:text-ink"
      }`}
    >
      Lijst
    </Link>
    <Link
      to={boardTo}
      className={`rounded px-3 py-1 transition ${
        view === "board"
          ? "bg-white text-brand shadow-card"
          : "text-ink-subtle hover:text-ink"
      }`}
    >
      Board
    </Link>
  </div>
);

const Chip = ({ children, onRemove, color }) => (
  <span className="inline-flex items-center gap-1.5 rounded bg-brand-subtle px-2 py-0.5 text-xs font-medium text-brand">
    {color ? <span className={`h-1.5 w-1.5 rounded-full ${color}`} /> : null}
    {children}
    <button
      type="button"
      onClick={onRemove}
      className="ml-0.5 -mr-0.5 rounded text-brand hover:bg-[#091E420F]"
      aria-label="Verwijder filter"
    >
      <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
        <path
          fillRule="evenodd"
          d="M4.3 4.3a1 1 0 011.4 0L10 8.6l4.3-4.3a1 1 0 111.4 1.4L11.4 10l4.3 4.3a1 1 0 11-1.4 1.4L10 11.4l-4.3 4.3a1 1 0 11-1.4-1.4L8.6 10 4.3 5.7a1 1 0 010-1.4z"
          clipRule="evenodd"
        />
      </svg>
    </button>
  </span>
);

const QUAL_LABEL = {
  qualified: "Geschikt",
  disqualified: "Handmatige beoordeling",
  incomplete: "Incompleet",
};
const BtwFilterBar = ({
  view,
  listTo,
  boardTo,
  filters,
  setFilter,
  resetFilters,
  statuses = [],
  admins = [],
  labels = [],
  currentUserId,
  showStatusFilter = true,
}) => {
  const [panelOpen, setPanelOpen] = useState(false);
  const activeCount = countActiveFilters(filters);

  const status = statuses.find((s) => s._id === filters.workflowStatusId);
  const admin = admins.find((a) => a._id === filters.assignedToAdminId);
  const label = labels.find((l) => l._id === filters.labelId);

  const chips = [];
  if (status) {
    const c = getStatusColor(status.color);
    chips.push(
      <Chip
        key="ws"
        color={c.dot}
        onRemove={() => setFilter("workflowStatusId", "")}
      >
        {status.name}
      </Chip>,
    );
  }
  if (filters.mine) {
    chips.push(
      <Chip key="mine" onRemove={() => setFilter("mine", false)}>
        Toegewezen aan mij
      </Chip>,
    );
  } else if (filters.unassigned) {
    chips.push(
      <Chip key="un" onRemove={() => setFilter("unassigned", false)}>
        Niet toegewezen
      </Chip>,
    );
  } else if (admin) {
    chips.push(
      <Chip key="adm" onRemove={() => setFilter("assignedToAdminId", "")}>
        {admin.naam}
      </Chip>,
    );
  }
  if (filters.qualificationStatus) {
    chips.push(
      <Chip
        key="q"
        onRemove={() => setFilter("qualificationStatus", "")}
      >
        {QUAL_LABEL[filters.qualificationStatus] ?? filters.qualificationStatus}
      </Chip>,
    );
  }
  if (filters.stuck) {
    chips.push(
      <Chip key="stuck" onRemove={() => setFilter("stuck", false)}>
        Vastgelopen
      </Chip>,
    );
  }
  if (filters.awaitingPartnerPayment) {
    chips.push(
      <Chip
        key="apn"
        onRemove={() => setFilter("awaitingPartnerPayment", false)}
      >
        Wacht op betaling installateur
      </Chip>,
    );
  }
  if (filters.toInvoice) {
    chips.push(
      <Chip key="tf" onRemove={() => setFilter("toInvoice", false)}>
        Te factureren
      </Chip>,
    );
  }
  if (label) {
    chips.push(
      <Chip key="lbl" onRemove={() => setFilter("labelId", "")}>
        {label.name}
      </Chip>,
    );
  }
  if (filters.deadline === "week") {
    chips.push(
      <Chip key="dl-w" onRemove={() => setFilter("deadline", "")}>
        Deadline deze week
      </Chip>,
    );
  } else if (filters.deadline === "overdue") {
    chips.push(
      <Chip key="dl-o" onRemove={() => setFilter("deadline", "")}>
        Overdue
      </Chip>,
    );
  }
  if (filters.archived === "true") {
    chips.push(
      <Chip key="arch" onRemove={() => setFilter("archived", "")}>
        Archief
      </Chip>,
    );
  }

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative">
          <svg
            className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-subtlest"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M9 3a6 6 0 104.47 10.03l3.25 3.25a.75.75 0 101.06-1.06l-3.25-3.25A6 6 0 009 3zm-4.5 6a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0z"
              clipRule="evenodd"
            />
          </svg>
          <input
            type="search"
            value={filters.search}
            onChange={(e) => setFilter("search", e.target.value)}
            placeholder="Zoek op naam of e-mail…"
            className="h-8 w-64 rounded border-2 border-line bg-white py-1 pl-8 pr-2 text-sm text-ink placeholder:text-ink-subtlest focus:border-brand focus:outline-none"
          />
        </div>

        <button
          type="button"
          onClick={() => setPanelOpen(true)}
          className={`inline-flex h-8 items-center gap-2 rounded px-3 text-sm font-medium transition ${
            activeCount > 0
              ? "bg-brand-subtle text-brand hover:bg-brand-subtler"
              : "bg-[#091E420F] text-ink hover:bg-[#091E4224]"
          }`}
        >
          <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
            <path
              fillRule="evenodd"
              d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3 5a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm3 5a1 1 0 011-1h2a1 1 0 110 2h-2a1 1 0 01-1-1z"
              clipRule="evenodd"
            />
          </svg>
          Filters
          {activeCount > 0 ? (
            <span className="ml-0.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded bg-brand px-1 text-[11px] font-bold text-white">
              {activeCount}
            </span>
          ) : null}
        </button>

        {chips.length > 0 ? (
          <div className="flex flex-wrap items-center gap-1.5">{chips}</div>
        ) : null}

        {chips.length > 0 ? (
          <button
            type="button"
            onClick={resetFilters}
            className="text-xs font-medium text-brand hover:underline"
          >
            Wis filters
          </button>
        ) : null}

        <div className="ml-auto">
          <ViewToggle view={view} listTo={listTo} boardTo={boardTo} />
        </div>
      </div>

      <BtwFiltersPanel
        isOpen={panelOpen}
        onClose={() => setPanelOpen(false)}
        filters={filters}
        setFilter={setFilter}
        resetFilters={resetFilters}
        statuses={statuses}
        admins={admins}
        labels={labels}
        currentUserId={currentUserId}
        showStatusFilter={showStatusFilter}
      />
    </>
  );
};

export default BtwFilterBar;

const DEFAULT_FILTERS = {
  search: "",
  workflowStatusId: "",
  assignedToAdminId: "",
  mine: false,
  unassigned: false,
  qualificationStatus: "",
  // Een "aanvraag" bestaat alleen na succesvolle Stripe-betaling. Pending /
  // abandoned records zijn afgebroken checkouts en horen niet in het
  // aanvragen-overzicht. Daarom is het status-filter hard op `paid`.
  status: "paid",
  stuck: false,
  awaitingPartnerPayment: false,
  toInvoice: false,
  labelId: "",
  deadline: "",
  archived: "",
};

export const useBtwFilters = (defaultOverrides = {}) => {
  const [filters, setFilters] = React.useState({
    ...DEFAULT_FILTERS,
    ...defaultOverrides,
  });
  const setFilter = (key, value) =>
    setFilters((prev) => ({ ...prev, [key]: value }));
  // Reset altijd naar pure defaults, niet naar de initiele overrides. Anders
  // blijven filters die via deep-link (?mine=1, ?stuck=1) of dashboard-tile
  // binnenkwamen, hangen na "Wis filters".
  const resetFilters = () => setFilters({ ...DEFAULT_FILTERS });
  return { filters, setFilter, resetFilters };
};
